import React, { useContext } from 'react';
import { Box, Button, Divider, Fade, IconButton, Stack, ThemeProvider, Typography } from '@mui/material';
import { colors } from '@roku-web-ui/rds-theme';

import { AppContext, AppDispatchContext } from 'context/app';
import { EventsFilterContext } from 'context/eventsFilter';
import { LeavesContext } from 'context/leaves';
import { useAspenLogger } from 'hooks/useAspenLogger';

import { Loading } from 'components/common/Loading';
import { CameraFilter } from 'components/events/filters/CameraFilter';
import { EventTypesFilter } from 'components/events/filters/EventTypesFilter';
import { DateFilter } from 'components/events/filters/DateFilter';
import { TimeFilter } from 'components/events/filters/TimeFilter';
import { IconClose } from 'components/icons';
import { SmartHomeLightTheme } from 'global/themes';

import styles from './eventsFilter.module.scss';

export const EventsFilter = () => {
  const { eventsFilterOpen } = useContext(AppContext);
  const dispatch = useContext(AppDispatchContext);
  const { logAspenEvent } = useAspenLogger();

  const { leafStore, loading: leavesLoading } = useContext(LeavesContext);
  const leaves = leafStore.read();

  const { applyFilter, resetFilter } = useContext(EventsFilterContext);

  return (
    <Box className={styles.eventsFilterContainer}>
      <ThemeProvider theme={SmartHomeLightTheme}>
        <Fade in={eventsFilterOpen}>
          <Stack className={styles.eventsFilter} direction="column" spacing={2}>
            <Stack
              className={styles.content}
              direction="column"
              spacing={2}
              divider={<Divider flexItem sx={{ borderColor: colors.grey[300], margin: '15px 0' }} />}
            >
              <Stack className={styles.header} direction="row" justifyContent="space-between">
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Filters
                </Typography>
                <IconButton
                  aria-label="filter-close"
                  data-testid="button-filter-close"
                  onClick={() => {
                    logAspenEvent({
                      log_id: 'iot.web.event.action',
                      payload: {
                        action: 'filter_close',
                      },
                    });

                    dispatch({
                      type: 'eventsFilterOpen',
                      payload: false,
                    });
                  }}
                  sx={{
                    alignSelf: 'center',
                    '&.MuiIconButton-root': {
                      padding: '0', // Override RDS padding
                    },
                    '&:hover': {
                      boxShadow: 'none',
                    },
                  }}
                >
                  <IconClose sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
              <Stack
                className={styles.scrollableFilters}
                direction="column"
                spacing={2}
                divider={<Divider flexItem sx={{ borderColor: colors.grey[300], margin: '15px 0' }} />}
              >
                <Stack className={styles.camerasSection} direction="column" spacing={2}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', margin: 0 }}>
                    Camera
                  </Typography>
                  {!leavesLoading && leaves ? <CameraFilter /> : <Loading />}
                </Stack>
                <>
                  <Stack className={styles.dateSection} direction="column" spacing={2}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', margin: 0 }}>
                      Date
                    </Typography>
                    <DateFilter />
                  </Stack>
                  <Stack className={styles.timeSection} direction="column" spacing={2}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', margin: 0 }}>
                      Time
                    </Typography>
                    <TimeFilter />
                  </Stack>
                </>
                <Stack className={styles.eventTypeSection} direction="column" spacing={2}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', margin: 0 }}>
                    Event Type
                  </Typography>
                  <EventTypesFilter />
                </Stack>
              </Stack>
            </Stack>
            <Stack className={styles.actions} direction="row" spacing={2} justifyContent="flex-end">
              <Button
                aria-label="clear all"
                data-testid="button-events-filter-clear"
                variant="outlined"
                onClick={() => {
                  resetFilter();
                }}
              >
                Clear all
              </Button>
              <Button
                aria-label="apply filters"
                data-testid="button-events-filter-apply"
                variant="contained"
                onClick={() => {
                  applyFilter();
                  dispatch({
                    type: 'eventsFilterOpen',
                    payload: false,
                  });
                }}
              >
                Apply
              </Button>
            </Stack>
          </Stack>
        </Fade>
      </ThemeProvider>
    </Box>
  );
};
